import { useCallback, useMemo } from 'react';

import { Input, Select, Skeleton } from 'antd';
import { useIntl } from 'react-intl';

import { CheckboxSegmented } from '@src/components/CheckboxSegmented';
import type { ElasticPaginatedSelectOption } from '@src/components/ElasticPaginatedSelect/ElasticPaginatedSelect.interfaces';
import ElasticUserSelect from '@src/components/ElasticUserSelect';
import { getElasticUserSelectionLabels } from '@src/components/ElasticUserSelect/buildElasticUserSelectOptions';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { DAYS_OF_WEEK } from '@src/constants/commons';
import IntlMessages from '@src/util/IntlMessages';

import { WORK_JOURNEY_STATUS } from '../../../constants';
import type { WorkJourneyWorkShift } from '../../../types';
import {
  buildWorkJourneyFilterSetValueProps,
  getWorkJourneyStateFilterLabel,
  WORK_JOURNEY_FILTER_DEFAULTS,
  WORK_JOURNEY_LIST_FILTER_FIELDS,
  WORK_JOURNEY_STATE_FILTER_OPTIONS,
} from '../../../workJourneyListFilterConfig';

const { Search } = Input;
const { Option } = Select;

const DEFAULT_DAYS_OPTIONS = DAYS_OF_WEEK.map(day => ({
  value: day.value,
  label: day.short,
}));

interface WorkJourneyFiltersProps {
  organizationId?: string;
  filters: FilterValues;
  handleFilters: (props: SetValueProps) => void;
  workShift: WorkJourneyWorkShift[];
  workShiftLoading: boolean;
}

/**
 * Filters for the work journey table (SecondaryPanel content).
 */
const WorkJourneyFilters = ({
  organizationId = '',
  filters,
  handleFilters,
  workShift,
  workShiftLoading,
}: WorkJourneyFiltersProps) => {
  const intl = useIntl();

  const rawDateRange = filters[WORK_JOURNEY_LIST_FILTER_FIELDS.DATE_RANGE]?.value;
  const dateRangeValue = Array.isArray(rawDateRange)
    ? rawDateRange.map(day => {
        const asNumber = Number(day);
        return Number.isNaN(asNumber) ? day : asNumber;
      })
    : [];

  const shiftValue = String(
    filters[WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT]?.value ?? WORK_JOURNEY_FILTER_DEFAULTS.SHIFT,
  );

  const stateValue = String(
    filters[WORK_JOURNEY_LIST_FILTER_FIELDS.STATE]?.value ?? WORK_JOURNEY_STATUS.ACTIVE,
  );

  const rawUserIds = filters[WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH]?.value;
  const selectedUserIds = useMemo(
    () =>
      Array.isArray(rawUserIds)
        ? rawUserIds.filter((id): id is string => typeof id === 'string')
        : [],
    [rawUserIds],
  );

  const handleUsersChange = useCallback(
    (
      userIds: string | string[] | undefined,
      options:
        | ElasticPaginatedSelectOption
        | ElasticPaginatedSelectOption[]
        | undefined,
    ) => {
      const nextIds = Array.isArray(userIds) ? userIds : [];
      const selectedLabels = getElasticUserSelectionLabels(nextIds, options);

      handleFilters(
        buildWorkJourneyFilterSetValueProps(
          WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH,
          nextIds,
          intl,
          selectedLabels.join(', '),
        ),
      );
    },
    [handleFilters, intl],
  );

  return (
    <div className="gx-flex-column gx-guarnic-gap-2">
      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="workJourney.filters.journeyName" />
        </label>
        <Search
          className="gx-m-0"
          value={String(filters[WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH]?.value ?? '')}
          onChange={e =>
            handleFilters(
              buildWorkJourneyFilterSetValueProps(
                WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH,
                e.target.value,
                intl,
              ),
            )
          }
          id={WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH}
        />
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="workJourney.filters.userSearch" />
        </label>
        <ElasticUserSelect
          mode="multiple"
          allowClear
          organizationId={organizationId}
          value={selectedUserIds}
          onChange={handleUsersChange}
          getPopupContainer={() => document.body}
        />
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="workJourney.filters.shifts" />
        </label>
        {workShiftLoading ? (
          <Skeleton active paragraph={false} />
        ) : (
          <Select
            allowClear
            className="gx-w-100"
            value={shiftValue}
            onChange={value => {
              const selectedShift = workShift.find(shift => shift.id === value);
              handleFilters(
                buildWorkJourneyFilterSetValueProps(
                  WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT,
                  value,
                  intl,
                  selectedShift?.name ?? intl.formatMessage({ id: 'workJourney.filters.all' }),
                ),
              );
            }}
            id={WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT}
          >
            <Option value={WORK_JOURNEY_FILTER_DEFAULTS.SHIFT}>
              <IntlMessages id="workJourney.filters.all" />
            </Option>
            {workShift.map(shift => (
              <Option key={shift.id} value={shift.id}>
                {shift.name}
              </Option>
            ))}
          </Select>
        )}
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="workJourney.filters.state" />
        </label>
        <Select
          className="gx-w-100"
          allowClear
          value={stateValue}
          onChange={value => {
            handleFilters(
              buildWorkJourneyFilterSetValueProps(
                WORK_JOURNEY_LIST_FILTER_FIELDS.STATE,
                value,
                intl,
              ),
            );
          }}
          id={WORK_JOURNEY_LIST_FILTER_FIELDS.STATE}
        >
          {WORK_JOURNEY_STATE_FILTER_OPTIONS.map(stateOption => (
            <Option key={stateOption} value={stateOption}>
              {getWorkJourneyStateFilterLabel(stateOption, intl)}
            </Option>
          ))}
        </Select>
      </div>

      <div>
        <label className="gx-mb-1 gx-d-block">
          <IntlMessages id="workJourney.filters.dateRange" />
        </label>
        <CheckboxSegmented
          fluid
          options={DEFAULT_DAYS_OPTIONS}
          value={dateRangeValue}
          onChange={value =>
            handleFilters(
              buildWorkJourneyFilterSetValueProps(
                WORK_JOURNEY_LIST_FILTER_FIELDS.DATE_RANGE,
                value,
                intl,
              ),
            )
          }
        />
      </div>
    </div>
  );
};

export default WorkJourneyFilters;
